import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLOTS = ["10:00", "11:00", "12:00", "15:00", "16:00", "17:00", "18:00", "19:00"];
const WEEKDAYS = {
  it: ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"],
  de: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
};

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function sameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function MiniCalendar({ value, onChange, locale }) {
  const { lang } = useLang();
  const today = startOfDay(new Date());
  const [view, setView] = useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const year = view.getFullYear();
  const month = view.getMonth();
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const canGoPrev = view > new Date(today.getFullYear(), today.getMonth(), 1);

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const monthLabel = view.toLocaleDateString(locale, { month: "long", year: "numeric" });

  return (
    <div data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => canGoPrev && setView(new Date(year, month - 1, 1))}
          disabled={!canGoPrev}
          data-testid="cal-prev"
          className="grid place-items-center w-9 h-9 rounded-lg border border-white/10 text-white/70 hover:border-cyan-brand hover:text-cyan-brand disabled:opacity-30 disabled:hover:border-white/10 disabled:hover:text-white/70 transition-colors duration-200"
        >
          <ChevronLeft size={16} />
        </button>
        <span className="font-display font-semibold text-white capitalize" data-testid="cal-month">{monthLabel}</span>
        <button
          type="button"
          onClick={() => setView(new Date(year, month + 1, 1))}
          data-testid="cal-next"
          className="grid place-items-center w-9 h-9 rounded-lg border border-white/10 text-white/70 hover:border-cyan-brand hover:text-cyan-brand transition-colors duration-200"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {WEEKDAYS[lang].map((w) => (
          <span key={w} className="text-center font-mono text-[10px] uppercase tracking-wider text-white/35 py-1">{w}</span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, i) => {
          if (!day) return <span key={`e${i}`} />;
          const disabled = day < today;
          const selected = sameDay(day, value);
          const isToday = sameDay(day, today);
          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={disabled}
              onClick={() => onChange(day)}
              data-testid={`cal-day-${day.getDate()}`}
              className={`h-9 rounded-lg text-sm font-medium transition-colors duration-150 ${
                selected
                  ? "bg-cyan-brand text-ink-bg font-bold"
                  : disabled
                  ? "text-white/20 cursor-not-allowed"
                  : `text-white/80 hover:bg-white/10 ${isToday ? "ring-1 ring-cyan-brand/50" : ""}`
              }`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function BookDemo() {
  const { t, lang } = useLang();
  const b = t.booking;
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [booked, setBooked] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", restaurant: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const locale = lang === "de" ? "de-DE" : "it-IT";

  useEffect(() => {
    if (!date) {
      setBooked([]);
      return;
    }
    let active = true;
    axios
      .get(`${API}/bookings/availability`, { params: { date: toISO(date) } })
      .then((res) => {
        if (!active) return;
        const taken = res.data.booked || [];
        setBooked(taken);
        setTime((cur) => (cur && taken.includes(cur) ? null : cur));
      })
      .catch(() => active && setBooked([]));
    return () => {
      active = false;
    };
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) {
      toast.error(!date ? b.selectDatePrompt : b.selectTimePrompt);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, { ...form, date: toISO(date), time, language: lang });
      toast.success(b.success);
      setForm({ name: "", email: "", restaurant: "", notes: "" });
      setDate(null);
      setTime(null);
    } catch {
      toast.error(b.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" className="relative py-24 md:py-36 border-t border-white/10 overflow-hidden">
      <div className="absolute inset-0 aurora opacity-40 -z-10" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal>
          <div className="max-w-2xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-coral-brand mb-5">{b.overline}</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tighter text-white leading-[0.95]">{b.title}</h2>
            <p className="mt-6 text-white/65 text-lg font-light">{b.sub}</p>
          </div>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          {/* Calendar + slots */}
          <Reveal>
            <div className="rounded-3xl border border-white/10 bg-ink-surface/50 p-6 md:p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 mb-4 flex items-center gap-2">
                <CalendarDays size={14} /> {b.dateLabel}
              </p>
              <MiniCalendar value={date} onChange={setDate} locale={locale} />

              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 mt-8 mb-3 flex items-center gap-2">
                <Clock size={14} /> {b.timeLabel}
              </p>
              <div className="grid grid-cols-4 gap-2" data-testid="demo-slots">
                {SLOTS.map((s) => {
                  const taken = booked.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      disabled={taken}
                      title={taken ? b.slotTaken : ""}
                      onClick={() => setTime(s)}
                      data-testid={`slot-${s}`}
                      className={`rounded-lg py-2 text-sm font-medium transition-colors duration-200 ${
                        taken
                          ? "border border-white/5 text-white/25 line-through cursor-not-allowed"
                          : time === s
                          ? "bg-cyan-brand text-ink-bg"
                          : "border border-white/10 text-white/70 hover:border-cyan-brand hover:text-cyan-brand"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="booking-form" className="rounded-3xl border border-white/10 bg-ink-surface/50 p-6 md:p-9 flex flex-col">
              {(date || time) && (
                <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-cyan-brand/30 bg-cyan-brand/10 px-5 py-3" data-testid="booking-chosen">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-brand">{b.chosen}</span>
                  <span className="font-display text-lg font-bold text-white">
                    {date ? date.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "long" }) : "—"}
                    {time ? ` · ${time}` : ""}
                  </span>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { k: "name", type: "text", required: true },
                  { k: "email", type: "email", required: true },
                  { k: "restaurant", type: "text", required: true },
                ].map((f) => (
                  <div key={f.k} className={f.k === "restaurant" ? "sm:col-span-2" : ""}>
                    <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mb-2">
                      {b[f.k]}{f.required && <span className="text-coral-brand"> *</span>}
                    </label>
                    <input
                      type={f.type}
                      required={f.required}
                      value={form[f.k]}
                      onChange={set(f.k)}
                      data-testid={`booking-${f.k}`}
                      className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder-white/30 focus:border-cyan-brand focus:outline-none transition-colors duration-200"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-5">
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mb-2">{b.notes}</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={set("notes")}
                  data-testid="booking-notes"
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder-white/30 focus:border-cyan-brand focus:outline-none transition-colors duration-200 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit"
                className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-coral-brand text-white font-semibold py-3.5 mt-6 hover:bg-coral-soft transition-colors duration-200 active:scale-95 disabled:opacity-60"
              >
                {loading ? b.sending : b.submit}
                {!loading && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
